/**
 * useMealPlan Hook - Wrapper for Meal Plan Store
 * 
 * Orchestrates the core AI planning experience, history, and favorites.
 * Handles cross-store updates (Macros, Inventory) during AI refinement.
 */

import { useCallback } from 'react';
import { useMealPlanStore } from '../stores/mealPlanStore';
import { Meal } from '../types';
import { useAppContextStore } from '../stores/appContextStore';

export function useMealPlan(currentUser: any, macrosHook: any, inventoryHook: any, uiHook: any) {
    // Actions are stable in Zustand if retrieved via granular selectors
    const storeProcessCommand = useMealPlanStore(state => state.processCommand);
    const storeHandleToggleFavorite = useMealPlanStore(state => state.handleToggleFavorite);
    const storeLoadHistory = useMealPlanStore(state => state.loadHistory);
    const storeLoadFavorites = useMealPlanStore(state => state.loadFavorites);
    const storeHandleDeleteHistory = useMealPlanStore(state => state.handleDeleteHistory);
    const storeHandleCommitPlan = useMealPlanStore(state => state.handleCommitPlan);
    const storeSetSelectedMeal = useMealPlanStore(state => state.setSelectedMeal);
    const storeSetCurrentPlan = useMealPlanStore(state => state.setCurrentPlan);
    const storeSetChefMessage = useMealPlanStore(state => state.setChefMessage);
    const storeLoadPlanDetails = useMealPlanStore(state => state.loadPlanDetails);
    const storeLoadFavoriteDetails = useMealPlanStore(state => state.loadFavoriteDetails);

    // State subscription
    const currentPlan = useMealPlanStore(state => state.currentPlan);
    const history = useMealPlanStore(state => state.recent_plans);
    const favorites = useMealPlanStore(state => state.favorites);
    const selectedMeal = useMealPlanStore(state => state.selectedMeal);
    const chefMessage = useMealPlanStore(state => state.chefMessage);
    const isLoading = useMealPlanStore(state => state.isLoading);

    const { setView, setIsUpgradeModalOpen, setUpgradeFeature, triggerToast } = uiHook;
    const { handleAutoMacroUpdate, handleAutoExclusionUpdate } = macrosHook;
    const { handleAutoInventoryUpdate } = inventoryHook;

    const getToken = useCallback(async () => {
        if (!currentUser) return null;
        return await currentUser.getIdToken();
    }, [currentUser]);

    const handleCommand = useCallback(async (command: string, mealSettings: any, dietPhilosophy?: string | null, macroProfile?: string | null, householdSize?: number) => {
        const token = await getToken();
        if (!token) return;

        try {
            await storeProcessCommand({
                token,
                command,
                mealSettings,
                onMacrosUpdate: handleAutoMacroUpdate,
                onExclusionsUpdate: handleAutoExclusionUpdate,
                onInventoryUpdate: handleAutoInventoryUpdate,
                onViewChange: (view) => setView(view),
                onUpgradeRequired: (feature) => {
                    setUpgradeFeature(feature);
                    setIsUpgradeModalOpen(true);
                },
                dietPhilosophy,
                macroProfile,
                householdSize // V13
            });
        } catch (err) {
            // Error handled in store (setting chefMessage)
        }
    }, [getToken, storeProcessCommand, handleAutoMacroUpdate, handleAutoExclusionUpdate, handleAutoInventoryUpdate, setView, setIsUpgradeModalOpen, setUpgradeFeature]);

    const handleToggleFavorite = useCallback(async (meal: Meal) => {
        const token = await getToken();
        if (!token) return;

        try {
            const message = await storeHandleToggleFavorite(token, meal);
            if (message) triggerToast(message);
            // Refresh app context
            useAppContextStore.getState().prefetch(token);
        } catch (err) {
            triggerToast("Failed to update favorites.");
        }
    }, [getToken, storeHandleToggleFavorite, triggerToast]);

    const loadHistory = useCallback(async () => {
        const token = await getToken();
        if (token) await storeLoadHistory(token);
    }, [getToken, storeLoadHistory]);

    const loadFavorites = useCallback(async () => {
        const token = await getToken();
        if (token) await storeLoadFavorites(token);
    }, [getToken, storeLoadFavorites]);

    const handleDeleteHistory = useCallback(async (planId: string) => {
        const token = await getToken();
        if (!token) return;

        try {
            await storeHandleDeleteHistory(token, planId);
            triggerToast("Plan deleted from history.");
        } catch (err) {
            triggerToast("Failed to delete plan.");
        }
    }, [getToken, storeHandleDeleteHistory, triggerToast]);

    const handleCommitPlan = useCallback(async (planId: string, overrides: any[] = [], skipWineDeductions: number[] = []) => {
        const token = await getToken();
        if (!token) return;

        try {
            await storeHandleCommitPlan(token, planId, overrides, skipWineDeductions);
            // Refresh app context (handles inventory refresh too)
            await useAppContextStore.getState().prefetch(token);
            triggerToast("Plan committed! 🚀");
        } catch (err) {
            triggerToast("Failed to commit plan.");
        }
    }, [getToken, storeHandleCommitPlan, triggerToast]);

    return {
        // State
        currentPlan,
        history,
        favorites,
        selectedMeal,
        chefMessage,
        isLoading,

        // Actions
        handleCommand,
        handleToggleFavorite,
        loadHistory,
        loadFavorites,
        handleDeleteHistory,
        handleCommitPlan,
        setSelectedMeal: storeSetSelectedMeal,
        setCurrentPlan: storeSetCurrentPlan,
        setChefMessage: storeSetChefMessage,
        loadPlanDetails: storeLoadPlanDetails,
        loadFavoriteDetails: storeLoadFavoriteDetails,
        cancelGeneration: useMealPlanStore(state => state.cancelGeneration),
        toggleWineSelection: useMealPlanStore(state => state.toggleWineSelection)
    };
}
